import React, { createRef, useCallback, useContext, useEffect, useState } from 'react';
import { defineMessages, WrappedComponentProps, injectIntl, MessageDescriptor, FormattedMessage } from 'react-intl';
import { NavigationType, useLocation, useNavigate, useNavigationType } from 'react-router-dom';
import { IPersonalDetails } from 'src/components/UserDetails/PersonalDetails/PersonalDetails';
import { IReservationRequiredFieldProps, IReservationResponse, populateReservations } from 'src/entities/reservation';
import { postReservation, reservationModelToRequest } from 'src/services/api/reservation';
import FormValidator from 'src/services/formValidator';
import { StepType } from '../../../services/getStepOrder';
import StepNumber from '../StepNumber/StepNumber';
import globalMessages from 'src/services/globalMessages';
import { IConfirmationLocationState } from '../ReservationConfirm/ReservationConfirm';
import FixedButton from 'src/components/form-components/FixedButton/FixedButton';
import { signupRules } from 'src/services/validations/signupRules';
import { Routes } from 'src/routes';
import { IPaymentSelectOption, PaymentProvider } from 'src/entities/pocket';
import { defaultRequiredInfo, PaymentType, ReservationOrder } from 'src/entities/company';
import { IPostPaymentReservationParams, postPaymentReservations, redirectToPaymentLink, reservationEndUserToPaymentUser, reservationModelToPaymentRequest } from 'src/services/api/paymentReservation';
import ReservationsSection from './sections/ReservationsSection/ReservationsSection';
import PersonalDetailsSection from './sections/PersonalDetailsSection/PersonalDetailsSection';
import PaymentSelectSection from './sections/PaymentSelectSection/PaymentSelectSection';
import UserInvoiceDetailsSection from './sections/UserInvoiceDetailsSection/UserInvoiceDetailsSection';
import StatementSection from './sections/StatementSection/StatementSection';
import ValidationMessage from 'src/components/form-components/ValidationMessage/ValidationMessage';
import { getPaymentNeedWithPrice, getServiceFromReservation, getServicesFromReservations, getSubtotal, isReservationEndUserDirty, isValidSubtotal, transformResponse } from './functions/helperFunctions';
import { createReservationEndUser, createValidation, createValidator } from './functions/createFunctions';
import { localStore } from 'src/services/storage';
import { redirectAfterSendReservation } from 'src/services/api/reservationConfirm';
import { AppContext } from 'src/contexts/AppContext';
import { BookingContext } from 'src/contexts/BookingFlowContext';
import { IReservationEndProvidedState, ReservationEndProvider } from 'src/contexts/ReservationEndContext';
import { IPaymentReservationResponse } from 'src/entities/paymentReservation';
import Popup from 'src/components/Popup/Popup';

const messages = defineMessages<string, MessageDescriptor>({
    sendReservation: {
        id: 'sendReservation',
        defaultMessage: 'Foglalás véglegesítése'
    },
    toPaymentSite: {
        id: 'toPaymentSite',
        defaultMessage: 'Tovább a fizetéshez'
    },
    reservationWarningHeader: {
        id: 'reservationWarningHeader',
        defaultMessage: 'Hiba a foglalások beküldésekor'
    },
    reservationWarningDefault: {
        id: 'reservationWarningDefault',
        defaultMessage: 'Általános hiba a foglalás véglegesítéskor'
    },
    reservationWarningCollision: {
        id: 'reservationWarningCollision',
        defaultMessage: 'Foglalás ütközés fizetendő foglalásnál'
    },
    reservationWarningPayment: {
        id: 'reservationWarningPayment',
        defaultMessage: 'Fizetési szolgáltatás hiba'
    }
});

type IReservationEndProps =
    WrappedComponentProps
    & {
        index: number;
        isEndStep: boolean;
        onEndStep: () => void;
        reservationOrder: ReservationOrder;
    }

export interface ISendReservationResponse {
    ReservedReservations: IReservationResponse[] | null,
    PaymentReservationResponse: IPaymentReservationResponse | null
}

const ReservationEnd: React.FC<IReservationEndProps> = ({ index, isEndStep, onEndStep, intl, reservationOrder }) => {

    const location = useLocation();
    const navigationType = useNavigationType();
    const navigate = useNavigate();
    const isReservationEndRoute = location.pathname === Routes.ReservationEnd;
    const { company, isAuthenticated, actions, user } = useContext(AppContext);
    const { reservationEndUser, reservations, activeReservationIndex,
        actions: { reloadReservations, setActiveReservation, handleUserChange, setReservationEndUser } } = useContext(BookingContext);
    const [selectedPayment, setSelectedPayment] = useState<IPaymentSelectOption | null>(null);
    const [sendCondition, setSendCondition] = useState(false);
    const [isWarningVisible, setIsWarningVisible] = useState(false);
    const [warningText, setWarningText] = useState(messages.reservationWarningDefault);

    const requiredFieldInfo: IReservationRequiredFieldProps = {
        isAuthenticated,
        requiredFieldInfo: company ? company.RequiredFieldInfo : defaultRequiredInfo
    }

    const sendReservation = useCallback(async () => {
        const postReservationParams = {
            user: reservationEndUser,
            reservations: reservations.map(reservationModelToRequest),
            shouldSignUp,
            termsAccepted: reservationEndUser.TermsAccepted,
            language: intl.locale,
            setUser: actions.setUser
        }

        if (!selectedPayment || selectedPayment.PaymentProvider === PaymentProvider.NoProvider || !isValidSubtotal(reservations)) {
            return postReservation(postReservationParams);
        }
        else {
            let postPaymentReservationParams: IPostPaymentReservationParams = {
                paymentReservations: reservations
                    .filter(reservation => {
                        const service = getServiceFromReservation(reservation);
                        return getPaymentNeedWithPrice(company?.PaymentSettings.IsPaymentEnabled, service);
                    })
                    .map(reservationModelToPaymentRequest),
                paymentUser: reservationEndUserToPaymentUser(reservationEndUser, selectedPayment),
                termsAccepted: reservationEndUser.TermsAccepted,
            }
            postReservationParams.reservations = postReservationParams.reservations
                .filter(reservation => !getPaymentNeedWithPrice(company?.PaymentSettings.IsPaymentEnabled, reservation.Service));

            if (postReservationParams.reservations.length === 0) {
                postPaymentReservationParams = {
                    ...postPaymentReservationParams,
                    user: reservationEndUser,
                    shouldSignUp,
                    setUser: actions.setUser
                }

                return postPaymentReservations(postPaymentReservationParams);
            }
            else {
                return Promise.all([postReservation(postReservationParams), postPaymentReservations(postPaymentReservationParams)]);
            }
        }

    }, [sendCondition, selectedPayment, reservations, reservationEndUser]);

    const afterSend = async (responseAny: any) => {
        setSendCondition(false);
        const response = transformResponse(responseAny);
        actions.setLoading(false);

        if (!response || (!response.PaymentReservationResponse && !response.ReservedReservations)) {
            showWarningPopup(messages.reservationWarningDefault);
            return;
        }

        if (response.PaymentReservationResponse) {
            if (response.PaymentReservationResponse.HasCollision) {
                showWarningPopup(messages.reservationWarningCollision);
                return;
            }

            if (response.PaymentReservationResponse.HasPaymentError) {
                showWarningPopup(messages.reservationWarningPayment);
                return;
            }
        }

        if (selectedPayment?.PaymentProvider === PaymentProvider.NoProvider || !response.PaymentReservationResponse) {
            const navigationState: IConfirmationLocationState =
            {
                UserEmail: reservationEndUser.Email,
                Reservations: response.ReservedReservations
                    ? populateReservations((response.ReservedReservations), getServicesFromReservations(reservations))
                    : [],
                didSignUp: shouldSignUp
            };
            redirectAfterSend = redirectAfterSendReservation(navigationState, company?.CustomSuccessLink);

            if (!redirectAfterSend) {
                navigate(Routes.Confirmation, { state: navigationState });
            }
        }
        else {
            if (response.ReservedReservations) {
                localStore.setItem('reservationResponse', JSON.stringify(response.ReservedReservations));
                localStore.setItem('didSignUp', JSON.stringify(shouldSignUp));
            }

            if (selectedPayment?.PaymentProvider === PaymentProvider.Barion 
                || selectedPayment?.PaymentProvider === PaymentProvider.SimplePay) {
                redirectToPaymentLink(response.PaymentReservationResponse.PaymentUrl);
            }
            else if (selectedPayment?.PaymentProvider === PaymentProvider.Paypal) {
                // TODO FINANCE redirect to PayPal    
            }
        }
    }

    const onSend = async () => {
        let validation = validator.personalValidator.validate(reservationEndUser).isValid;

        if (company?.PaymentSettings.IsBillingEnabled && selectedPayment?.NeedsInvoice) {
            validation = validation && (validator.invoiceValidator.validate(reservationEndUser.InvoiceDetails).isValid);
        }

        const signUpValidation = !shouldSignUp || new FormValidator(signupRules).validate(reservationEndUser).isValid;
        setSubmitted(true);

        if (!validation || !signUpValidation || submitting) {
            setSendCondition(false);
            return;
        }

        setSendCondition(true);
        submitting = true;
        actions.setLoading(true);
        redirectAfterSend = false;
    }

    useEffect(() => {
        if (sendCondition) {
            sendReservation().then(
                afterSend,
                (reason) => {
                    setErrorMessage(reason.errorMessage);
                    actions.setLoading(false);
                    submitting = false;
                    setSendCondition(false);
                }
            );
        }
    }, [sendCondition])

    const [shouldSignUp, setShouldSignUp] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [validator, setValidator] = useState(createValidator(requiredFieldInfo, shouldSignUp, !!company?.TermsAndConditionsLink));
    const [hasOnlinePocket, setHasOnlinePocket] = useState(false);
    const [errorMessage, setErrorMessage] = useState<MessageDescriptor | undefined>();

    const personalDetailsRef = createRef<IPersonalDetails>();
    const personalValidation = createValidation(validator.personalValidator, reservationEndUser, submitted)
    const invoiceValidation = createValidation(validator.invoiceValidator, reservationEndUser.InvoiceDetails, submitted)

    const showWarningPopup = (warningTextValue: MessageDescriptor) => {
        setWarningText(warningTextValue);
        setIsWarningVisible(true);
    }

    const closeWarningPopup = () => {
        setIsWarningVisible(false);
        navigate(Routes.HomeSlash);
    }

    useEffect(() => {
        if (!isReservationEndUserDirty(reservationEndUser)) {
            setReservationEndUser(createReservationEndUser(user));
        }
        if (personalDetailsRef.current) {
            personalDetailsRef.current.reset();
        }
    }, [user]);

    useEffect(() => {
        setValidator(createValidator(requiredFieldInfo, shouldSignUp, !!company?.TermsAndConditionsLink));
    }, [isAuthenticated, shouldSignUp]);

    useEffect(() => {
        if (location && location.state && location.state.reservationModels
            && location.state.reservationEndUser && location.state.selectedPaymentId && !isEndStep) {
            onEndStep();
            setReservationEndUser({ ...location.state.reservationEndUser, PrivacyStatementAccepted: user?.PrivacyStatementAccepted ?? false });
            setSelectedPayment({ Id: location.state.selectedPaymentId, Name: '', NeedsInvoice: false })
            reloadReservations(reservationOrder, location.state.reservationModels);
            location.state.reservationModels = undefined;
            location.state.reservationEndUser = undefined;
            location.state.selectedPaymentId = undefined;
        }
        else if (isReservationEndRoute && !isEndStep) {
            navigate({
                pathname: Routes.Book,
                search: location.search
            });
        }
    }, [isEndStep]);

    useEffect(() => {
        return () => {
            if (navigationType === NavigationType.Pop && location.pathname === Routes.Book) {
                setActiveReservation(activeReservationIndex);
            }
        };
    }, [location, navigationType])

    useEffect(() => {
        setErrorMessage(undefined);
    }, [reservationEndUser, shouldSignUp])
    

    if (!isEndStep || !reservations) {
        return null;
    }

    const displayedSubtotal = company?.PaymentSettings.IsPaymentEnabled ? getSubtotal(reservations) : null;
    let redirectAfterSend: boolean = false;
    let submitting: boolean = false;

    const reservationEndContext: IReservationEndProvidedState = {
        hasOnlinePocket,
        selectedPayment,
        shouldSignUp,
        displayedSubtotal,
        actions: {
            setHasOnlinePocket,
            setSelectedPayment,
            setShouldSignUp
        }
    }

    if (!isReservationEndRoute) {
        return null;
    }

    return (
        <ReservationEndProvider value={reservationEndContext}>
            <section>
                <StepNumber name={globalMessages[StepType.ReservationEnd]} index={index + 1} touched={true} />
                <div className="reservation-end">

                    <ReservationsSection />

                    <PersonalDetailsSection
                        ref={personalDetailsRef}
                        validation={personalValidation}
                        submitted={submitted}
                    />

                    <PaymentSelectSection />

                    <UserInvoiceDetailsSection
                        validation={invoiceValidation}
                    />

                    <StatementSection
                        validation={personalValidation}
                        setDataPolicy={(value) => handleUserChange(value, 'PrivacyStatementAccepted')}
                        setTermsAccepted={(value) => handleUserChange(value, 'TermsAccepted')}
                    />

                    <div className="validation-message-container">
                        <ValidationMessage message={errorMessage} />
                    </div>
                    <FixedButton
                        className="reservation-end__send-button"
                        caption={(company?.PaymentSettings.PaymentType === PaymentType.NeedPay && hasOnlinePocket &&
                            displayedSubtotal && displayedSubtotal?.Price !== 0) ?
                            messages.toPaymentSite : messages.sendReservation}
                        onClick={onSend}
                        form={'reservation-end-form'}
                    />
                </div>
            </section>
            <Popup closePopup={closeWarningPopup} visible={isWarningVisible}>
                <section className="confirmation">
                    <h2 className="confirmation__header">
                        <FormattedMessage {...messages.reservationWarningHeader} />
                    </h2>
                    <p className='confirmation__warning'>
                        <FormattedMessage {...warningText} />
                    </p>
                </section>
            </Popup>
        </ReservationEndProvider>
    );
}

export default injectIntl(ReservationEnd);
